<template>
  <q-drawer
    class="right-sidebar"
    v-model="drawerRight"
    side="right"
    :width="windowWidth > 1025 ? 400 : windowWidth"
    persistent
  >
    <div class="right-sidebar__container">
      <div
        class="flex items-center justify-between no-wrap q-px-md bg-background-very-light --border-bottom --shadow__bottom"
        style="height: 66px; flex-shrink: 0"
      >
        <div class="flex items-center">
          <q-icon name="sym_r_auto_awesome" size="20px" class="q-mr-sm" />
          <h6 class="q-mb-none q-ml-xs">Ask CarScience</h6>
        </div>
        <q-btn
          flat
          round
          icon="sym_r_close"
          color="text-background-very-light"
          @click="$store.commit('chat/SET_SIDEBAR', false)"
          data-nosnippet
        />
      </div>

      <div class="right-sidebar__messages-container">
        <ChatMessages
          ref="ChatMessages"
          :chat-messages="chatMessages"
          empty-placeholder="Type a message below to get personalized<br/>recommendations and advice."
          :receiving-message="receivingMessage"
          dropdown-title-descriptor="Scoring Overview"
        >
          <template v-slot:below-messages>
            <div v-if="windowWidth <= 1025">
              <div
                  v-show="!receivingMessage && chatMessages.length > 0 && chatMessages.length % 2 === 0 && !searchLoading && $store.state.search.pagination.totalResults > 0"
                  class="q-mx-md q-my-md"
              >
                <q-btn
                    color="secondary"
                    rounded
                    no-caps
                    class="full-width"
                    @click="() => { drawerRight = false; }"
                >
                  <template v-slot:default>
                    See Your Results {{ !searchLoading ? '(' + formatNumber($store.state.search.pagination.totalResults) + ')' : '' }}
                  </template>
                </q-btn>
              </div>
            </div>
          </template>
        </ChatMessages>
      </div>

      <div class="right-sidebar__input-bar">
        <div
          v-if="suggestedResponses.length > 0 && !receivingMessage"
          class="q-pb-md column items-end"
        >
          <div
            class="--text-sm text-weight-medium text-text-light q-mb-xs q-mr-sm"
          >
            Suggestions For You
          </div>

          <div class="flex items-center justify-end">
            <q-chip
              v-for="(suggestedResponse, i) in suggestedResponses"
              :key="i + '_suggestedResponse'"
              color="background-very-light"
              text-color="white"
              @click.prevent.stop="
                () => {
                  sendUserChatMessage(suggestedResponse);
                }
              "
              clickable
              class="suggested-response --text-sm --shadow__bottom --bordered ellipsis"
              style="height: auto; padding: 4px 8px"
            >
              <div class="ellipsis">{{ suggestedResponse }}</div>
            </q-chip>
          </div>
        </div>

        <AiInputBar
          :disable="disableUserInput"
          @submitted="
            e => {
              sendUserChatMessage(e.input);
            }
          "
          class=""
          add-shadows
        />
        <div
          class="text-gray-medium-dark q-mt-sm text-center"
          style="font-size: 10px;"
        >
          CarScience AI can make mistakes, double-check all responses.
        </div>
      </div>
    </div>
  </q-drawer>
</template>

<script>
import ChatMessages from "@/components/UI/ChatMessages.vue";
import AiInputBar from "@/components/UI/AiInputBar.vue";
import axios from "axios";
import {mapGetters} from "vuex";

export default {
  name: "TheChatSidebar",
  components: { AiInputBar, ChatMessages },
  data() {
    return {
      disableUserInput: false,
      receivingMessage: false,
      pendingFiltersRequests: [],
      pendingScoringRequests: [],
      suggestedResponses: [
        "Best for Daily Driving",
        "Well-Equipped Sedans",
        "Used AWD SUVs",
        "Cheap Vehicles"
      ],
      gemini: null
    };
  },
  computed: {
    ...mapGetters("search", [
      "searchLoading"
    ]),
    drawerRight: {
      get() {
        return this.$store.state.chat.sidebarOpen;
      },
      set(value) {
        this.$store.commit("chat/SET_SIDEBAR", value);
      }
    },
    chatMessages() {
      return this.$store.state.chat.messages;
    },
    conversationHistory() {
      return this.$store.state.chat.conversationHistory;
    },
    scoringConversationHistory() {
      return this.$store.state.chat.scoringConversationHistory;
    },
    totalUnreadMessages() {
      let lastSeenTotal = this.$store.state.chat.numReceivedMessagesSeen;
      return (
        this.chatMessages.filter(message => !message.fromUser).length -
        lastSeenTotal
      );
    }
  },
  watch: {
    chatMessages: {
      handler() {
        if (this.chatMessages.at(-1) && this.chatMessages.at(-1).fromUser) {
          this.receiveChatBotMessage(this.chatMessages.at(-1).message);
        }
      },
      deep: true
    },
    searchLoading() {
      setTimeout(() => {
        this.$refs["ChatMessages"].scrollToBottom();
      }, 50);
    }
  },
  created() {},
  unmounted() {
    this.pendingFiltersRequests.forEach(controller => {
      controller.abort();
    });
    this.pendingScoringRequests.forEach(controller => {
      controller.abort();
    });
  },
  methods: {
    receiveChatBotMessage(message = "") {
      this.disableUserInput = true;
      this.receivingMessage = true;
      this.suggestedResponses = [];

      let thisMessage = {
        fromUser: false,
        message: "",
        dropdownContent: ""
      };

      let thisSearchFilters = [];
      let thisSearchScoring = [];
      let thisSearchSorting = [];
      let thisSuggestedResponses = [];

      let fetchFilters = new Promise(resolve => {
        if (this.pendingFiltersRequests.length > 0) {
          this.pendingFiltersRequests.forEach(controller => {
            controller.abort();
          });
          this.pendingFiltersRequests = [];
        }
        let filtersController = new AbortController();
        this.pendingFiltersRequests.push(filtersController);

        axios({
          url:
            "https://api-v2.jumpfeed.com/network/publisher/carscience-search-chatbot-open-router-v2",
          method: "POST",
          signal: filtersController.signal,
          data: {
            last_search_filters: this.$store.state.search.searchFilters,
            conversation_history: this.conversationHistory,
            message: message.substring(0, 500), // trim to prevent potential mass inputs
            visitor_profile: this.generateVisitorProfile()
          }
        })
          .then(response => {
            let output = response.data.data;

            thisMessage.message = output.reply.reasoning;

            thisSearchFilters =
              "filters" in output.reply.search_request_object
                ? output.reply.search_request_object.filters
                : [];
            thisSearchSorting =
              "sorting" in output.reply.search_request_object
                ? output.reply.search_request_object.sorting
                : [];
            thisSuggestedResponses = output.reply.suggested_responses;

            if (
              "options" in output.reply.search_request_object &&
              typeof output.reply.search_request_object.options === "object" &&
              "location_search" in output.reply.search_request_object.options
            ) {
              let newLocationSearch =
                output.reply.search_request_object.options.location_search;
              let postalCodeUpdated = false;
              let radiusUpdated = false;
              if (
                "postal_code" in newLocationSearch &&
                newLocationSearch.postal_code !=
                  this.$store.state.search.location.postalCode
              ) {
                postalCodeUpdated = true;
              }
              if (
                "radius" in newLocationSearch &&
                newLocationSearch.radius !=
                  this.$store.state.search.location.radius
              ) {
                radiusUpdated = true;
              }

              if (postalCodeUpdated || radiusUpdated) {
                if (!postalCodeUpdated) {
                  newLocationSearch.postal_code = this.$store.state.search.location.postalCode;
                }
                this.$store
                  .dispatch(
                    "geolocation/VALIDATE_POSTAL_CODE",
                    newLocationSearch.postal_code
                  )
                  .then(locationData => {
                    this.$store.commit("search/SET_LOCATION", {
                      postalCode: postalCodeUpdated
                        ? newLocationSearch.postal_code
                        : this.$store.state.search.location.postalCode,
                      city: locationData["city"],
                      state: locationData["state"],
                      radius: radiusUpdated
                        ? newLocationSearch.radius
                        : this.$store.state.search.location.radius
                    });
                  });
              }
            }

            this.$store.commit(
              "chat/SET_CONVERSATION_HISTORY",
              output.conversation
            );

            window.localStorage.setItem(
              "previous-suggested-responses",
              JSON.stringify(thisSuggestedResponses)
            );
          })
          .catch(e => {
            console.error(e);
            thisMessage.message =
              "Oops, something went wrong. Please try again.";
          })
          .finally(() => {
            resolve();
          });
      });

      let fetchScoring = new Promise(resolve => {
        if (this.pendingScoringRequests.length > 0) {
          this.pendingScoringRequests.forEach(controller => {
            controller.abort();
          });
          this.pendingScoringRequests = [];
        }
        let scoringController = new AbortController();
        this.pendingScoringRequests.push(scoringController);

        axios({
          url:
            "https://api-v2.jumpfeed.com/network/publisher/carscience-search-ai-scoring-v2",
          method: "POST",
          signal: scoringController.signal,
          data: {
            conversation_history: this.scoringConversationHistory,
            message: message.substring(0, 500), // trim to prevent potential mass inputs
            visitor_profile: {
              clickCounts: this.generateVisitorProfile(),
              filterCounts: this.$store.getters[
                "search/searchFiltersHistoryByClauseCount"
              ],
              filterHistory: this.$store.getters[
                "search/searchFiltersHistory"
              ].slice(0, 20)
            }
          }
        })
          .then(response => {
            let output = response.data.data;
            this.$store.commit(
              "chat/SET_SCORING_CONVERSATION_HISTORY",
              output.conversation
            );

            thisMessage.dropdownContent = output.reply.reasoning;

            thisSearchScoring =
              "scoring" in output.reply.scoring_object
                ? output.reply.scoring_object.scoring
                : [];

            window.localStorage.setItem(
              "carscience-visitor-profile-scoring",
              JSON.stringify(thisSearchScoring)
            );
          })
          .catch(e => {
            console.error(e);
            thisMessage.dropdownContent =
              "Oops, something went wrong. Please try again.";
          })
          .finally(() => {
            resolve();
          });
      });

      Promise.all([fetchFilters, fetchScoring]).then(() => {
        let performSearchUpdate =
          thisSearchFilters.length > 0 || thisSearchSorting.length > 0;

        if (thisMessage.message.length > 0) {
          if (!performSearchUpdate) {
            thisMessage.dropdownContent = "";
          }
          this.$store.commit("chat/ADD_MESSAGE", thisMessage);
        }

        this.suggestedResponses = thisSuggestedResponses;

        if (performSearchUpdate) {
          this.$store.commit("search/SET_SEARCH_FILTERS", thisSearchFilters);
          this.$store.commit("search/SET_SEARCH_SORTING", thisSearchSorting);
          this.$store.commit("search/SET_SEARCH_SCORING", thisSearchScoring);

          this.$store.commit("search/RESET_PAGINATION");
          this.$store.commit("search/SET_LOADING", true);

          if (this.$route.name !== "search") {
            this.$router.push({ name: "search" });
          }
        }

        this.receivingMessage = false;
        this.disableUserInput = false;
      });
    },
    sendUserChatMessage(message) {
      if (message === "") return;

      this.$store.commit("chat/ADD_MESSAGE", {
        fromUser: true,
        message
      });

      // this.receiveChatBotMessage(message);
      // this.generateSpeech(message);
    }
  }
};
</script>

<style lang="scss">
.right-sidebar {
  background-color: color(background, light);
  box-shadow: $shadow-bottom;
  border-left: 1px solid color(border);
  height: 100%;
  z-index: 3001 !important;
  margin-left: -1px;

  @include screen-md {
    margin-left: 0;
  }

  .right-sidebar__container {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  .right-sidebar__messages-container {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
  }

  .right-sidebar__input-bar {
    padding: $space-sm $space-md;
    flex-shrink: 0;

    @include screen-md {
      margin-bottom: 4px;
    }
  }

  .suggested-response {
    @include hover-scale(1.065);
  }
}
</style>
